import { useState } from "react";
import { CheckCircle2, X } from "lucide-react";
import { ITI_TRADES } from "../data/itiTrades";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ApplicationDialog({ job, onClose }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", education: "", iti_trade: "", city: "", message: "" });
  const [resumeName, setResumeName] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setStatus("loading");
    setError("");
    try {
      if (form.education === "Degree" && !resumeName) {
        throw new Error("Please select your PDF resume to continue.");
      }
      const response = await fetch(`${API}/applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, job_id: job.id, resume_filename: resumeName }),
      });
      if (!response.ok) throw new Error("We could not submit your application.");
      setStatus("success");
    } catch (requestError) {
      setError(requestError.message);
      setStatus("idle");
    }
  };

  const handleResume = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf" || file.size > 5 * 1024 * 1024) {
      setResumeName("");
      setError("Please choose a PDF resume smaller than 5 MB.");
      event.target.value = "";
      return;
    }
    setError("");
    setResumeName(file.name);
  };

  return (
    <div className="dialog-backdrop" role="presentation" data-testid="application-dialog-backdrop">
      <section className="application-dialog" role="dialog" aria-modal="true" aria-labelledby="application-title" data-testid="application-dialog">
        <button className="dialog-close" type="button" onClick={onClose} aria-label="Close application form" data-testid="application-close-button"><X size={20} /></button>
        {status === "success" ? (
          <div className="success-state" data-testid="application-success-message">
            <CheckCircle2 size={46} />
            <h2>Application received!</h2>
            <p>Your details have been shared for <strong>{job.title}</strong>. The hiring team may contact you soon.</p>
            <button type="button" className="primary-button" onClick={onClose} data-testid="application-success-close-button">Done / हो गया</button>
          </div>
        ) : (
          <>
            <span className="dialog-eyebrow">Quick application / त्वरित आवेदन</span>
            <h2 id="application-title">Apply for {job.title}</h2>
            <p className="dialog-subtitle">{job.company} · {job.location}</p>
            <form className="application-form" onSubmit={submit} data-testid="application-form">
              <label>Full name / पूरा नाम<input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} data-testid="application-name-input" /></label>
              <label>Phone number / फोन नंबर<input required type="tel" pattern="[0-9+\-\s]{10,18}" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} data-testid="application-phone-input" /></label>
              <label>Email address / ईमेल<input required type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} data-testid="application-email-input" /></label>
              <label>Highest education / शिक्षा<select required value={form.education} onChange={(event) => setForm({ ...form, education: event.target.value, iti_trade: event.target.value === "ITI" ? form.iti_trade : "" })} data-testid="application-education-select"><option value="">Choose one</option><option>ITI</option><option>Diploma</option><option>12th Pass</option><option>Degree</option><option>Other</option></select></label>
              {form.education === "ITI" && <label>ITI trade / आईटीआई ट्रेड<select required value={form.iti_trade} onChange={(event) => setForm({ ...form, iti_trade: event.target.value })} data-testid="application-iti-trade-select"><option value="">Choose your ITI trade</option>{ITI_TRADES.map((trade) => <option key={trade}>{trade}</option>)}</select></label>}
              {form.education === "Degree" && <div className="degree-note" data-testid="degree-apprenticeship-message"><strong>Degree Apprenticeship</strong><span>You are applying for a Degree Apprenticeship. A PDF resume is required.</span></div>}
              {form.education === "Degree" && <label>Resume (PDF, max 5 MB)<input required type="file" accept="application/pdf" onChange={handleResume} data-testid="application-resume-input" /></label>}
              <label>Current city / शहर<input required value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} data-testid="application-city-input" /></label>
              <label>Message (optional)<textarea value={form.message} onChange={(event) => setForm({ ...form, message: event.target.value })} data-testid="application-message-input" /></label>
              {error && <p className="form-error" data-testid="application-error-message">{error}</p>}
              <button className="primary-button" disabled={status === "loading"} data-testid="application-submit-button">{status === "loading" ? "Sending…" : "Submit application / आवेदन भेजें"}</button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}